<script setup>
import {MagnifyingGlassIcon, XMarkIcon, MapPinIcon, InformationCircleIcon, MapIcon} from "@heroicons/vue/24/outline";
import useKeyboardInput from "@/composables/useKeyboardInput";
import {defineProps, defineEmits, ref, watch, onMounted, inject, unref} from "vue";
import {performRequest} from "@/API/Services/APIService";
import APIEndpoints from "@/API/Services/APIEndpoints";
import debounce from "lodash/debounce";
import LoadingSpinner from "@/components/ui/LoadingSpinner.vue";
import Devider from "@/components/ui/Devider.vue";
import JobFilterButton from "@/components/ui/JobFilterButton.vue";
import RangeFilterButton from "@/components/ui/RangeFilterButton.vue";
import ToggleButton from "@/components/ui/ToggleButton.vue";
import LoadingBlock from "@/components/ui/LoadingBlock.vue";
import EmptyState from "@/components/ui/EmptyState.vue";
import IconButton from "@/components/ui/IconButton.vue";
import SearchBar from "@/components/ui/SearchBar.vue";
import EventBus from "@/EventBus";

const text = ref("")
const selectedLocation = ref(null)
const locations = ref({
  isLoading: false,
  show: false,
  results: [],
})

const companies = ref({
  isLoading: false,
  show: false,
  isOpen: true,
  results: [],
})

const selectedJobs = ref([])
const selectedRange = ref(5)

const onMarkerClick = inject("onMarkerClick")
const navigateMapTo = inject("navigateMapTo")


const fetchLocations = async (query) => {
  locations.value.isLoading = true

  const viewbox = "7.85,49.60,10.50,47.35";
  const url = `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(query)}&addressdetails=1&limit=5&viewbox=${viewbox}&bounded=1`;

  fetch(url, { headers: {
      'Accept-Language': 'de',
      'User-Agent': 'Ausbildungsplatzbörse/1.0 (info@studiomerkas.com)'
    }})
      .then(res => res.json())
      .then(data => {
        locations.value.results = data;
        locations.value.show = true
      })
      .catch(error => {
        console.error(error);
        locations.value.results = []
      })
      .finally(() => {
        locations.value.isLoading = false
      })
}

const debouncedSearch = debounce(fetchLocations, 500)

const fetchCompanies = () => {

  companies.value.show = true
  companies.value.isLoading = true

  const jobs = unref(selectedJobs)
  const range = unref(selectedRange)

  let query = `?limit=1000&range=${range}`

  if(selectedLocation.value) {
    const boundingBox = JSON.stringify(selectedLocation.value.boundingbox)
    const coordinates = JSON.stringify([selectedLocation.value.lon, selectedLocation.value.lat])
    const placeRank = selectedLocation.value.place_rank
    query += `&boundingBox=${boundingBox}&coordinates=${coordinates}&placeRank=${placeRank}`
  }

  if(jobs.length > 0) {
    query += `&jobs=${JSON.stringify(jobs)}`
  }
  else {
    query += `&jobs=all`
  }

  performRequest(APIEndpoints.GET_COMPANIES, { query })
      .then(data => {
        companies.value.results = data.companies
      })
      .catch(error => {
        console.log(error.meta)
      })
      .finally(() => {
        companies.value.isLoading = false
        companies.value.isOpen = true
      })
}

const onLocationClick = (location) => {
  text.value = location['display_name']
  selectedLocation.value = location
  locations.value.show = false

  fetchCompanies()
}

const onInput = (text) => {

  companies.value.show = false

  EventBus.emit("inputEvent", text)

  if(text.length < 3) {
    locations.value.results = []
    locations.value.show = false
    locations.value.isLoading = false
    return
  }

  locations.value.show = true

  debouncedSearch(text);

}

const onEnter = (el) => {
  el.style.height = '0'
  el.style.opacity = '0'

  requestAnimationFrame(() => {
    el.style.transition = 'height 300ms ease, opacity 200ms ease'
    el.style.height = el.scrollHeight + 'px'
    el.style.opacity = '1'
  })
}

const onAfterEnter = (el) => {
  el.style.height = 'auto'
  el.style.transition = ''
}

const onLeave = (el) => {
  el.style.height = el.scrollHeight + 'px'
  el.style.opacity = '1'

  requestAnimationFrame(() => {
    el.style.transition = 'height 0ms ease, opacity 0ms ease'
    el.style.height = '0'
    el.style.opacity = '0'
  })
}

</script>

<template>

  <div class="flex flex-col gap-y-4 w-full z-[300]">

    <div class="h-fit glass-background group flex flex-col gap-y-2 !px-0 py-4 focus-within">

      <!-- Searchbar -->
      <SearchBar v-model="text" @input="onInput" />

      <Transition
          @enter="onEnter"
          @after-enter="onAfterEnter"
          @leave="onLeave"
      >

        <div v-if="locations.show" class="overflow-hidden">

          <Devider />

          <!-- Lade Spinner -->
          <LoadingBlock v-if="locations.isLoading" />


          <!-- Locations -->
          <div v-else-if="locations.results.length > 0" class="flex flex-col gap-y-2 pt-2">

            <h3 class="popup-section-title px-4">Filter</h3>

            <div class="mx-4 flex gap-x-2">
              <JobFilterButton v-model="selectedJobs"/>
              <RangeFilterButton v-model="selectedRange"/>
            </div>

            <Devider />

            <h3 class="px-4 popup-section-title">Orte</h3>

            <ul class="w-full space-y-2 h-fit max-h-60 overflow-auto flex flex-col scrollbar-custom">
              <li v-for="location in locations.results" :key="location.place_id">
                <button @click="onLocationClick(location)" class="py-2 text-start px-4 shadow-none gap-x-2 flex items-center !flex-row hover:bg-black/5 w-full">
                  <MapPinIcon class="size-5 shrink-0"/>
                  {{ location['display_name'] }}
                </button>
              </li>
            </ul>

          </div>

          <EmptyState v-else>
            <template #title>
              Keine Orte gefunden für "{{ text }}"!
            </template>
            <template #message>
              Bitte überprüfe die Schreibweise oder suche nach einem anderen Ort.
            </template>
          </EmptyState>

        </div>

      </Transition>

    </div>

    <div v-if="companies.show" class="h-fit z-[500] glass-background group flex flex-col gap-y-2 !px-0 py-4 overflow-hidden">

        <LoadingBlock v-if="companies.isLoading" />

        <div v-else-if="companies.results.length > 0">

          <div class="px-4 flex justify-between items-center">
            <h3>Ausbildungsbetriebe <span>{{companies.results.length}}</span></h3>
            <ToggleButton v-model:is-open="companies.isOpen" />
          </div>

          <Transition
              enter-active-class="transition-opacity duration-200"
              enter-from-class="opacity-0"
              enter-to-class="opacity-100"
              leave-active-class="transition-opacity duration-200"
              leave-from-class="opacity-100"
              leave-to-class="opacity-0"
          >
            <Devider v-show="companies.isOpen"/>
          </Transition>

            <ul
              class="w-full overflow-auto !scrollbar-thin scrollbar-thumb-neutral-700 scrollbar-track-transparent transition-[max-height,opacity] duration-300 ease-in-out"
              :class="companies.isOpen ? 'max-h-60 opacity-100' : 'max-h-0 opacity-0'"
          >
            <li class="w-full h-12  flex items-center justify-between gap-3 ps-4 pe-1" v-for="entry in companies.results" :key="entry._id">
              <span class="normalText">{{entry.company.companycaption1}}</span>

              <div class="flex items-center gap-3">
                <IconButton :icon="InformationCircleIcon" @click="onMarkerClick(entry)" />

                <IconButton :icon="MapIcon" @click="navigateMapTo(entry); companies.isOpen = false" />

              </div>

            </li>

          </ul>

        </div>

        <EmptyState v-else>
          <template #title>
            Keine Ausbildungsbetriebe gefunden!
          </template>
          <template #message>
            Bitte überprüfe die Schreibweise oder suche nach einem anderen Ort.
          </template>
        </EmptyState>

      </div>

  </div>


</template>

<style scoped>

</style>